import {
  Button,
  EmptyState,
  ListPanel,
  MenuItem,
  SearchField,
  SelectField,
  Stack,
  TopicList,
  TopicListItemSkeleton,
} from '@all-manager/design-system-v2';
import type { StatusFilter, TopicListPage } from '../../pages/useTopicListPage';
import TopicRow from '../TopicRow/TopicRow';
import TopicsPagination from '../TopicsPagination/TopicsPagination';
import { useTopicListPanel } from './useTopicListPanel';

const SKELETON_ROWS = [0, 1, 2, 3, 4];

type Props = {
  page: TopicListPage;
};

export default function TopicListPanel(props: Props) {
  const page = props.page;
  const hook = useTopicListPanel(page);

  return (
    <ListPanel
      fill
      toolbar={(
        <Stack spacing={1}>
          <SearchField
            value={page.topicSearch}
            onChange={page.setTopicSearch}
            label="Rechercher un sujet"
            placeholder="Rechercher un sujet"
          />
          <SelectField<StatusFilter>
            label="Statut"
            size="small"
            fullWidth
            value={page.topicStatusFilter}
            onChange={hook.handleStatusFilterChange}
          >
            <MenuItem value="active">Actifs</MenuItem>
            <MenuItem value="archived">Archivés</MenuItem>
            <MenuItem value="all">Tous</MenuItem>
          </SelectField>
        </Stack>
      )}
      footer={page.showTopicPagination ? (
        <TopicsPagination
          count={page.totalTopics}
          page={page.topicPage}
          rowsPerPage={page.topicPerPage}
          onPageChange={page.setTopicPage}
          onRowsPerPageChange={page.setTopicPerPage}
        />
      ) : undefined}
    >
      {page.isTopicsError ? (
        <EmptyState
          title="Sujets indisponibles"
          subtitle="Le chargement a échoué. Rechargez la page pour réessayer."
        />
      ) : page.isTopicsLoading ? (
        <TopicList aria-label="Sujets" aria-busy>
          {SKELETON_ROWS.map((row) => <TopicListItemSkeleton key={row} />)}
        </TopicList>
      ) : hook.isEmpty && page.hasTopicFilters ? (
        <EmptyState
          title="Aucun résultat"
          subtitle={hook.noResultSubtitle}
          action={(
            <Button variant="text" size="small" onClick={page.resetTopicFilters}>
              Effacer les filtres
            </Button>
          )}
        />
      ) : hook.isEmpty ? (
        <EmptyState
          title="Aucun sujet"
          subtitle="Les sujets regroupent les sous-sujets de la copropriété : travaux, entretien, juridique…"
        />
      ) : (
        <TopicList aria-label="Sujets">
          {page.topics.map((topic) => <TopicRow key={topic.id} page={page} topic={topic} />)}
        </TopicList>
      )}
    </ListPanel>
  );
}
